import {
    useCallback,
    forwardRef,
    useRef
} from "react";
import {
    TextInput as NativeTextInput,
    type TextInput,
    Animated,
    Platform,
    View
} from "react-native";
import type IPopoverSurfaceSearchProps from "./type";
import type {
    PopoverSurfaceSearchWebPropsType,
    IPopoverSurfaceSearchRef
} from "./type";
import stylesheet, {
    getPopoverSurfaceSearchSize,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    RefForwardingComponent,
    NCoreUIKitIcon
} from "../../../../types";
import {
    getAppearStyle,
    usePresence
} from "../../../../helpers/motion";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    Search as SearchIcon,
    X as XIcon
} from "lucide-react-native";
import AnimatedIcon from "../../../animatedIcon";
import Button from "../../../button";

const PopoverSurfaceSearchLeadingIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <SearchIcon
    color={customColor}
    size={size}
/>;

const PopoverSurfaceSearchClearIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    animation="signature"
    icon={XIcon}
    size={size}
/>;

const PopoverSurfaceSearch: RefForwardingComponent<IPopoverSurfaceSearchRef, IPopoverSurfaceSearchProps> = ({
    icon: IconComponentProp = PopoverSurfaceSearchLeadingIcon,
    "aria-autocomplete": ariaAutocomplete = "list",
    "aria-activedescendant": ariaActiveDescendant,
    "aria-controls": ariaControls,
    "aria-haspopup": ariaHasPopup,
    "aria-label": ariaLabel,
    role = "combobox",
    size = "medium",
    customLocalize,
    customTheme,
    inputStyle,
    onClear,
    style,
    value,
    ...props
}, ref) => {
    const {
        controlPaddings,
        controlHeights,
        touchTargets,
        typography,
        iconSizes,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const {
        isReduceMotionEnabled,
        isRendered,
        progress
    } = usePresence({
        isVisible: !!onClear && !!value,
        enterIntent: "iconAppear",
        exitIntent: "iconAppear",
        customTheme
    });

    const inputRef = useRef<TextInput>(null);

    const currentSize = getPopoverSurfaceSearchSize({
        controlPaddings,
        controlHeights,
        touchTargets,
        pointerType,
        iconSizes,
        size
    });

    const {
        container: containerDynamicStyle,
        action: actionDynamicStyle,
        input: inputDynamicStyle,
        icon: iconDynamicStyle
    } = useStyles({
        currentSize,
        pointerType,
        typography,
        borders,
        colors,
        spaces
    });

    const handleInputRef = useCallback((node: TextInput | null) => {
        inputRef.current = node;

        if(typeof ref === "function") {
            ref(node);

            return;
        }

        if(ref) {
            ref.current = node;
        }
    }, [
        ref
    ]);

    const handleClear = () => {
        onClear?.();

        inputRef.current?.focus();
    };

    const webProps: PopoverSurfaceSearchWebPropsType = Platform.OS === "web" ? {
        "aria-activedescendant": ariaActiveDescendant,
        "aria-autocomplete": ariaAutocomplete,
        "aria-controls": ariaControls,
        "aria-haspopup": ariaHasPopup
    } : {};

    const renderClear = () => {
        if(!isRendered) {
            return null;
        }

        return <Animated.View
            style={[
                actionDynamicStyle,
                getAppearStyle({
                    isReduceMotionEnabled,
                    progress
                })
            ]}
        >
            <Button
                accessibilityLabel={localize("clear-search")}
                icon={PopoverSurfaceSearchClearIcon}
                size={currentSize.buttonSize}
                customTheme={customTheme}
                spreadBehaviour="free"
                onPress={handleClear}
                variant="ghost"
                type="neutral"
            />
        </Animated.View>;
    };

    return <View
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        <View
            style={[
                stylesheet.icon,
                iconDynamicStyle
            ]}
            aria-hidden={true}
        >
            <IconComponentProp
                customColor={colors.content.icon.mid}
                size={currentSize.iconSize}
                color="mid"
            />
        </View>
        <NativeTextInput
            autoCapitalize="none"
            enterKeyHint="search"
            autoCorrect={false}
            inputMode="search"
            {...props}
            {...webProps}
            placeholderTextColor={colors.content.text.low}
            aria-label={ariaLabel ?? localize("search")}
            underlineColorAndroid="transparent"
            style={[
                inputStyle,
                stylesheet.input,
                inputDynamicStyle
            ]}
            ref={handleInputRef}
            multiline={false}
            value={value}
            role={role}
        />
        {renderClear()}
    </View>;
};
export default forwardRef(PopoverSurfaceSearch);
